import { ReactElement, ReactNode, useMemo } from 'react'
import type { SvgIconComponent } from '@mui/icons-material'
import { Form2 } from '@ui/forms'
import type { QsFilterKind } from './components/ListFilterButton'
import Results2ElementListInner from './components/Results2ElementListInner'
import { useListViewState } from './hooks/useListViewState'
import type { Results2ListItem, SortMode } from './types'

export type { Results2ListItem } from './types'

export type Results2ElementListProps = {
  items: Results2ListItem[]
  selectedId?: string
  selectedIds?: string[]
  /** Return false when navigation/selection must be aborted (caller shows a warning). */
  onSelect?: (id: string) => boolean | void
  /** Return false when navigation/selection must be aborted (caller shows a warning). */
  onSelectionChange?: (ids: string[]) => boolean | void
  multiSelectEnabled?: boolean
  groupingEnabled?: boolean
  /** Sort modes this list offers; defaults to the full set. */
  sortModes?: SortMode[]
  isLoading?: boolean
  emptyMessage?: string
  onCalculate?: () => void
  /** Cancels in-flight results loading; surfaced as an "Abbrechen" footer button. */
  onAbort?: () => void
  /** When true, shows the "Abbrechen" button next to "Berechnen". */
  showAbort?: boolean
  onBlockpad?: () => void
  blockpadSlot?: ReactNode
  showBlockpadButton?: boolean
  calculateSlot?: ReactNode
  /** Copy the parent row’s cross section onto a grouped child (wall rips/lintels, beams, etc.). */
  onCopyParentCrossSection?: (childId: string, parentId: string) => void
  copyParentCrossSectionVisibleByChildId?: Record<string, boolean>
  copyParentCrossSectionLoadingChildId?: string | null
  /** When false, hides the comment area (list column and icons). Default true. */
  showCommentColumn?: boolean
  /** When false, hides the Vorbemerkungen sub-column (lists whose items have one comment). */
  showExtendedCommentColumn?: boolean
  /**
   * Lets the `qs` line use the whole first column. Compact lists cap it at a cross-section's
   * width; the Schubanker list holds an anchor name there instead.
   */
  qsFullWidth?: boolean
  /** Placeholder of the comment column field (default "Positionsliste..."). */
  commentPlaceholder?: string
  /** Which suggestion pool the comment field offers — anchors keep their own (default position). */
  commentSuggestionKind?: 'position' | 'anchor'
  /**
   * Tooltip of the sort option for the rows' trailing metric (anchors: the design tensile load).
   * Only lists that name it offer that sort mode.
   */
  metricSortLabel?: string
  /** Set false for lists whose rows have no η_c,90 (anchors), hiding that sort option. */
  etaCSortEnabled?: boolean
  /**
   * Renames the sort option for the `qs` column, whose meaning is list-specific — a cross section
   * for member lists, the chosen anchor for the anchors list.
   */
  qsSortOption?: { label: string; icon: SvgIconComponent }
  /** Names the `qs` filter after what the column holds: a cross section (default) or an anchor. */
  qsFilterKind?: QsFilterKind
  /** Offers the foot/head filter; the rows then carry their `shearAnchorPosition`. */
  showShearAnchorPositionFilter?: boolean
  /** Anchors list: autosave comment edits via the manual-loads endpoint instead of grouping. */
  anchorCommentAutosave?: boolean
  /** Controls whether the footer shows "Bauteile" / "Wände" 3D-target buttons. Default true. */
  showThreeDTargetButtons?: boolean
  /**
   * When true (Standardrippen / Wandscheiben compact list), top-level rows show `assemblyName`
   * on the actions row instead of the exportiert control.
   */
  hideExportButton?: boolean

  /** “Fokus” toggle — parent uses this for camera framing. */
  fokusCamera: boolean
  onFokusCameraChange: (next: boolean) => void
  /** “Transparenz” toggle — isolates selection in 3D. */
  fokusTransparency: boolean
  onFokusTransparencyChange: (next: boolean) => void

  disableForm?: boolean
  elementType: ElementTypes
}

export type Results2ElementListFormValues = {
  items: Results2ListItem[]
}

const Results2ElementList = ({
  items,
  elementType,
  ...rest
}: Results2ElementListProps): ReactElement => {
  const defaultValues = useMemo<Results2ElementListFormValues>(() => ({ items }), [items])

  // View state (sort, filters, expansion) lives above Form2 so it stays stable when the form
  // reinitializes on incoming `items` changes (e.g. the autosave echo during drag-and-drop
  // grouping) — keeping it here decouples the view from the form's data lifecycle.
  const viewState = useListViewState()

  const key = useMemo(() => JSON.stringify(defaultValues), [defaultValues])

  return (
    <Form2<Results2ElementListFormValues>
      defaultValues={defaultValues}
      style={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}
      onSubmit={() => {
        // Autosave is driven by the debounced list watcher; submit-on-change is enabled so this
        // component can be progressively migrated to field-level registrations.
      }}
      key={key}
    >
      <Results2ElementListInner elementType={elementType} viewState={viewState} {...rest} />
    </Form2>
  )
}

export default Results2ElementList
